/* Result words for a Search row — card r9ClOD0okEnZ (email/user report:
   "memory results return 'Untitled'").

   A Memory is a typed entry: its words live in `body`, its `title` is null and
   its `url` is not a page, so the old label fell through to 'Untitled' and the
   row said nothing at all about the card it had found. The label chain below
   ends at the body before it ever says 'Untitled', and a title-less typed card
   gets its body as the second line too — the row has to carry the card, not a
   placeholder.

   Pure on purpose: the screen renders what this returns, and scripts/
   probe-search-label.mjs drives the same functions the bundle uses. */

export interface LabelledItem {
  url: string;
  title: string | null;
  description: string | null;
  body: string | null;
  entryType: string;
}

export interface ResultWords {
  label: string;
  /** Second line. Empty when there is nothing extra worth saying. */
  preview: string;
  /** Which searchable field the preview came from, so the row can mark it. */
  previewField: 'description' | 'body' | null;
}

export const LABEL_CHARS = 80;
export const PREVIEW_CHARS = 160;

const squash = (s: string): string => s.replace(/\s+/g, ' ').trim();

/** The first non-empty line, collapsed — a card's own headline. */
function firstLine(s: string | null | undefined): string {
  if (!s) return '';
  for (const line of s.split('\n')) {
    const t = squash(line);
    if (t) return t;
  }
  return '';
}

function clip(s: string, max: number): string {
  if (s.length <= max) return s;
  return s.slice(0, max - 1).replace(/[\s,;:.-]+$/, '') + '…';
}

type LabelSource = 'title' | 'host' | 'description' | 'body' | 'none';

function labelOf(item: LabelledItem): { text: string; source: LabelSource } {
  const title = item.title?.trim();
  if (title) return { text: title, source: 'title' };
  /* A hostname is only a label for something that IS a page. A memory's url is
     not, and parsing it would either throw or print a scheme nobody typed. */
  if (/^https?:\/\//i.test((item.url ?? '').trim())) {
    try {
      const host = new URL(item.url).hostname.replace(/^www\./, '');
      if (host) return { text: host, source: 'host' };
    } catch {
      /* fall through to the card's own words */
    }
  }
  const desc = firstLine(item.description);
  if (desc) return { text: clip(desc, LABEL_CHARS), source: 'description' };
  const body = firstLine(item.body);
  if (body) return { text: clip(body, LABEL_CHARS), source: 'body' };
  return { text: 'Untitled', source: 'none' };
}

export function itemLabel(item: LabelledItem): string {
  return labelOf(item).text;
}

/**
 * What the row says besides its label. The description is preferred when the
 * saver wrote one; a typed card that only has a body shows the body IN FULL
 * (bounded) so the result is the card, not a teaser — but never twice: when the
 * label already came from the body, there is nothing extra to say.
 */
export function describeResult(item: LabelledItem): ResultWords {
  const { text: label, source } = labelOf(item);
  const desc = squash(item.description ?? '');
  if (desc && desc !== label) {
    return { label, preview: desc, previewField: 'description' };
  }
  const body = squash(item.body ?? '');
  if (body && source !== 'body' && body !== label) {
    return { label, preview: clip(body, PREVIEW_CHARS), previewField: 'body' };
  }
  return { label, preview: '', previewField: null };
}
